import type { ReactNode } from 'react';
import React, { useState, useRef, useEffect } from 'react';

/**
 * Compute which `nodes` can fit into `maxWidth` and which overflow because there is not enough width.
 *
 * @param maxWidth The total maximum width available to render nodes
 * @param nodes Nodes to check. <Key, Value> pair where Key is a unique identifier, and Value is the node to render.
 * @returns `visible` nodes which must be rendered, `overflowed` nodes which can be rendered e.g. in an overflow menu
 */
export const useOverflow = (maxWidth: number, nodes: Record<string, ReactNode>) => {
	const checkedKeys = useRef(new Set<string>());
	const overflowedKeys = useRef(new Set<string>());
	const [render, setRender] = useState(0); // Not real state; just used to trigger a re-render

	let remainingWidth = maxWidth;

	const ref = (key: string) => {
		return (element: HTMLElement | null) => {
			if (element && !checkedKeys.current.has(key) && maxWidth >= 0) {
				checkedKeys.current.add(key);

				const width = element.offsetWidth;
				if (width <= remainingWidth) {
					remainingWidth -= width;
					element.style.position = 'inherit';
					element.style.visibility = 'visible';
				} else {
					overflowedKeys.current.add(key);
					element.style.position = 'absolute';
					element.style.visibility = 'hidden';
				}
			}
		};
	};

	useEffect(() => {
		const overflowedKeysCurrent = overflowedKeys.current;
		const checkedKeysCurrent = checkedKeys.current;

		if (checkedKeysCurrent.size > 0) {
			if (overflowedKeysCurrent.size > 0) {
				setRender(render + 1);
			}

			return () => {
				overflowedKeysCurrent.clear();
				checkedKeysCurrent.clear();
			};
		}
	}, [render, nodes, maxWidth]);

	const getVisibleItem = (key: string) => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		<div key={key} ref={ref(key)} style={{ display: 'inline-block' }}>
			{nodes[key]}
		</div>
	);

	const getOverflowedItem = (key: string) => (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		<div key={key} style={{ display: 'block' }}>
			{nodes[key]}
		</div>
	);

	const keys = Object.keys(nodes);

	return {
		visible: keys.filter((key) => !overflowedKeys.current.has(key)).map(getVisibleItem),
		overflowed: keys.filter((key) => overflowedKeys.current.has(key)).map(getOverflowedItem),
	};
};
